import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { updatePassword, signOut } from 'firebase/auth';
import { auth } from '../firebase';

const ChangePasswordPage = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) {
        setError('You must be logged in to change your password.');
        setLoading(false);
        return;
      }

      // Update password in Firebase Auth
      await updatePassword(user, newPassword);

      // Clear the passwordResetRequired flag in Firestore. Force-refresh the ID token first —
      // updatePassword can leave the cached token stale, which makes this call fail auth.
      // Best-effort: ProtectedRoute is the source of truth, so if the flag is still set it will
      // bounce the user back here rather than let them through with a stale flag.
      try {
        const idToken = await user.getIdToken(true);
        const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3800';
        const response = await fetch(`${API_BASE_URL}/auth/clear-password-reset`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        });
        if (!response.ok) {
          console.warn('Failed to clear password reset flag:', response.status);
        }
      } catch (clearErr) {
        console.warn('Error clearing password reset flag:', clearErr);
      }

      // Success - show inline confirmation, then redirect. Use a hard navigation: a client-side
      // navigate reuses ProtectedRoute's stale passwordResetRequired state and bounces back here,
      // whereas a full load re-fetches the (now-cleared) flag and lands on the dashboard.
      setSuccess(true);
      setTimeout(() => { window.location.assign('/'); }, 1200);
    } catch (err) {
      console.error('Error changing password:', err);
      if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else if (err.code === 'auth/requires-recent-login') {
        setError('For security reasons, please log out and log back in, then try changing your password again.');
      } else {
        setError('Failed to change password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">
            Change Password Required
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Your password has been reset by an administrator. Please choose a new password to continue.
          </p>
        </div>
        
        <form className="space-y-6" onSubmit={handleChangePassword}>
          <div className="relative">
            <label
              htmlFor="newPassword"
              className="block text-sm font-medium text-gray-700"
            >
              New Password
            </label>
            <input
              id="newPassword"
              name="newPassword"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              className="block w-full px-3 py-2 mt-1 text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Enter new password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 flex items-center px-3 pt-6 text-sm text-gray-600"
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>

          <div className="relative">
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-700"
            >
              Confirm New Password
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              className="block w-full px-3 py-2 mt-1 text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute inset-y-0 right-0 flex items-center px-3 pt-6 text-sm text-gray-600"
            >
              {showConfirmPassword ? 'Hide' : 'Show'}
            </button>
          </div>

          {success && (
            <div className="p-3 text-sm text-green-800 rounded-md bg-green-50">
              Your password has been changed successfully. Taking you to your dashboard…
            </div>
          )}

          {error && (
            <div className="p-3 text-sm text-red-700 rounded-md bg-red-50">{error}</div>
          )}

          <div className="space-y-3">
            <button
              type="submit"
              disabled={loading || success}
              className="flex justify-center w-full px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {success ? 'Password Changed' : loading ? 'Changing Password...' : 'Change Password'}
            </button>
            
            <button
              type="button"
              onClick={handleLogout}
              className="flex justify-center w-full px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 border border-transparent rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Log Out
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
